import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useCreateFeedback } from "@workspace/api-client-react";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

type Category = "bug" | "general" | "feature";

const MAX_LEN = 2000;
const MIN_LEN = 10;

const CATEGORIES: { value: Category; label: string; description: string }[] = [
  { value: "bug",     label: "Bug",     description: "Something isn't working" },
  { value: "general", label: "General", description: "Just want to say something" },
  { value: "feature", label: "Feature", description: "An idea for what to build next" },
];

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const { currentUser } = useAuth();
  const { profile } = useUserProfile();
  const { toast } = useToast();

  const [category, setCategory] = useState<Category>("general");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { mutate, isPending } = useCreateFeedback();

  useEffect(() => {
    if (isOpen) {
      setCategory("general");
      setMessage("");
      setEmail(profile?.notifyEmail ?? currentUser?.email ?? "");
      setError(null);
    }
  }, [isOpen, profile?.notifyEmail, currentUser?.email]);

  if (!isOpen) return null;

  const trimmed = message.trim();
  const remaining = MAX_LEN - message.length;
  const isSignedIn = Boolean(currentUser);
  const canSubmit =
    !isPending &&
    trimmed.length >= MIN_LEN &&
    trimmed.length <= MAX_LEN &&
    (isSignedIn || email.trim().length > 0);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    mutate(
      {
        data: {
          category,
          message: trimmed,
          contactEmail: email.trim() || null,
          pageUrl: typeof window !== "undefined" ? window.location.href : null,
        },
      },
      {
        onSuccess: () => {
          // Close immediately and surface a global thank-you toast.
          onClose();
          toast({
            title: "Thanks — got it.",
            description: "We read every note and will take a look soon.",
          });
        },
        onError: (err: unknown) => {
          const msg =
            err instanceof Error ? err.message : "Couldn't send feedback. Please try again.";
          setError(msg);
        },
      },
    );
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/50 px-4 py-6 backdrop-blur-sm"
      onClick={onClose}
      data-testid="feedback-modal"
    >
      <div
        className="my-auto max-h-[calc(100vh-3rem)] w-full max-w-md overflow-y-auto rounded-3xl border border-stone-200 bg-white p-7 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-stone-900">Send feedback</h2>
            <p className="mt-0.5 text-xs text-stone-500">
              We read every note. Thanks for helping QuotedsArrow get better.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-stone-400 transition hover:bg-stone-100 hover:text-stone-600"
            aria-label="Close"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
            <fieldset>
              <legend className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                Category
              </legend>
              <div className="grid grid-cols-3 gap-2">
                {CATEGORIES.map((c) => {
                  const active = category === c.value;
                  return (
                    <button
                      type="button"
                      key={c.value}
                      onClick={() => setCategory(c.value)}
                      className={
                        "rounded-2xl border px-3 py-2.5 text-left text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 " +
                        (active
                          ? "border-stone-900 bg-stone-900 text-white"
                          : "border-stone-200 bg-white text-stone-700 hover:bg-stone-50")
                      }
                      data-testid={`feedback-category-${c.value}`}
                      aria-pressed={active}
                    >
                      <span className="block text-sm font-medium">{c.label}</span>
                      <span
                        className={
                          "mt-0.5 block text-[11px] " + (active ? "text-stone-300" : "text-stone-400")
                        }
                      >
                        {c.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <div>
              <label htmlFor="feedback-message" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                Message
              </label>
              <textarea
                id="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX_LEN))}
                rows={5}
                placeholder={
                  category === "bug"
                    ? "What happened, and what did you expect?"
                    : category === "feature"
                    ? "Describe the idea — what would it let you do?"
                    : "What's on your mind?"
                }
                className="w-full resize-none rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 focus:border-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-200"
                data-testid="feedback-message"
                required
                minLength={MIN_LEN}
                maxLength={MAX_LEN}
              />
              <p className="mt-1 text-right text-[11px] text-stone-400">
                {trimmed.length < MIN_LEN
                  ? `${MIN_LEN - trimmed.length} more character${MIN_LEN - trimmed.length === 1 ? "" : "s"}`
                  : `${remaining} left`}
              </p>
            </div>

            <div>
              <label htmlFor="feedback-email" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                Contact email {isSignedIn ? <span className="font-normal normal-case text-stone-400">(optional)</span> : <span className="font-normal normal-case text-stone-400">(required)</span>}
              </label>
              <input
                id="feedback-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-2xl border border-stone-200 bg-white px-4 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:border-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-200"
                data-testid="feedback-email"
                required={!isSignedIn}
                maxLength={320}
              />
            </div>

            {error && (
              <p className="rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700" role="alert">
                {error}
              </p>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-2xl px-4 py-2.5 text-sm font-medium text-stone-500 transition hover:bg-stone-100 hover:text-stone-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="rounded-2xl bg-stone-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-300"
                data-testid="feedback-submit"
              >
                {isPending ? "Sending…" : "Send"}
              </button>
            </div>
          </form>
      </div>
    </div>,
    document.body,
  );
}
